// Patient self-test start. One path, large type, because many users have low vision:
//   1. Check my screen     (browser report vs the approved monitors, or another monitor's EDID)
//   2. Identify my monitor (once: a small helper reads every connected monitor's EDID)
//   3. Measure brightness  (the lux meter on a white screen, guided step by step; compared
//                           with this monitor's baseline, or saved as its first one)
//   4. Start the test      (one meter reading per session: none after the last test)
// Every failed check comes with click-for-click steps for this computer (from the server,
// fix_steps.py), one problem at a time: the first is open, the rest folded below it. On a
// phone or in a browser other than Chrome only that is shown, since nothing else can pass.
// In debug mode with "skip the monitor checks" (an admin's switch for one patient) steps 1-3
// are recorded, not enforced.

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type Plan, type Qualification } from "../api";
import { CardCheck } from "../components/CardCheck";
import { FixCheck, HELPER_KEYS, MonitorHelper } from "../components/HomeChecks";
import { LuxGuide } from "../components/LuxGuide";
import { errorText } from "../hooks";
import { probe, screenDetails, type ProbeReport } from "../lib/probe";

export function HomeTestPage() {
  const patientId = Number(useParams().id);
  const nav = useNavigate();
  const [params] = useSearchParams();
  // a later program in the same sitting: the visit it belongs to (one screen check and lux reading)
  const visit = params.get("visit");
  const [plan, setPlan] = useState<Plan | null>(null);
  const [chosen, setChosen] = useState<number | null>(params.get("program") ? Number(params.get("program")) : null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState<Qualification | null>(null);
  const [report, setReport] = useState<ProbeReport | null>(null);
  const [lux, setLux] = useState<number[] | null>(null);     // the reading the server accepted
  const [fullGuide, setFullGuide] = useState(false);
  const [distanceOk, setDistanceOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [luxSkipped, setLuxSkipped] = useState(false);
  const [card, setCard] = useState<number | "skipped" | null>(null);   // the scale matched, or skipped

  // the first-time setup comes first (it opens by itself until it is done or skipped)
  useEffect(() => {
    if (visit) return;
    api.mySetup().then((st) => { if (st.needed) nav("/setup", { replace: true }); }, () => undefined);
  }, [visit, nav]);
  useEffect(() => {
    api.myPlan(visit).then((p) => {
      setPlan(p);
      setChosen((c) => c ?? p.run[0] ?? null);
    }, (e) => setError(errorText(e)));
  }, [visit]);
  // continuing a visit: check the screen straight away (it is the same screen, a minute later)
  useEffect(() => {
    if (visit && plan && chosen !== null && !q && !busy) void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit, plan, chosen]);

  const check = async () => {
    setBusy(true);
    setError(null);
    setLux(null);            // a reading belongs to the screen it was taken on
    setLuxSkipped(false);
    setCard(null);
    setFullGuide(false);
    // Chrome asks once to "manage windows on all your displays". Its question is small and
    // at the top-left; if no answer comes quickly, say where to look. Unanswered for a
    // minute: carry on without the screen's name (that check then says how to allow it).
    const hint = window.setTimeout(() => setAsking(true), 1500);
    try {
      const d = await Promise.race([screenDetails(),
        new Promise<null>((r) => window.setTimeout(() => r(null), 60_000))]);
      window.clearTimeout(hint);
      setAsking(false);
      const r = await probe(d?.current ?? null);
      setReport(r);
      setQ(await api.qualify(r, { program_id: chosen, visit_id: visit }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      window.clearTimeout(hint);
      setAsking(false);
      setBusy(false);
    }
  };

  const start = async () => {
    if (!q?.display_profile_id || !report) return;
    setBusy(true);
    try {
      const s = await api.createSession({
        patient_id: patientId, mode: "home", display_profile_id: q.display_profile_id, program_id: chosen, visit_id: visit,
        client_info: { ...report, brightness: { readings: lux ?? [], phase: q.brightness?.phase ?? null },
          distance_confirmed: distanceOk, card_check: typeof card === "number" ? { scale: card } : null },
      });
      nav(`/sessions/${s.id}/run`);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409
        ? "The checks did not pass when starting. Please check your screen again."
        : errorText(e));
      setQ(null);
      setLux(null);
    } finally {
      setBusy(false);
    }
  };

  const failed = q?.checks.filter((c) => c.critical && !c.ok) ?? [];
  const skipped = q?.checks.filter((c) => c.skipped_for_demo && !c.ok) ?? [];
  // On a phone or in another browser nothing else can pass yet: show only that.
  const root = failed.find((c) => c.key === "desktop") ?? failed.find((c) => c.key === "browser");
  const shown = root ? [root] : failed.filter((c) => !HELPER_KEYS.includes(c.key));
  // The helper is offered whenever its checks failed, whatever else did.
  const helperFailed = q?.checks.filter((c) => HELPER_KEYS.includes(c.key) && !c.ok) ?? [];
  const needMonitor = !!q && !root && helperFailed.some((c) => c.critical
    || (c.key === "monitor_id" && q.monitor_id?.mode !== "off"));
  const brightnessOk = !q?.brightness || q.brightness.phase === "visit" || lux !== null || luxSkipped;
  // recommended checks that did not pass: shown with their fixes; the patient may go on
  const advised = q?.checks.filter((c) => c.level === "recommended" && !c.ok && !HELPER_KEYS.includes(c.key)) ?? [];
  const distanceLevel = q?.requirements?.distance ?? "mandatory";
  const cardNeeded = !!q?.card && q.card.phase === "check";
  const cardOk = !cardNeeded || (typeof card === "number" && Math.abs(card - 1) <= (q?.card?.tolerance ?? 0.05))
    || (card === "skipped" && q?.card?.level !== "mandatory");
  const nameOf = (id: number) => plan?.items.find((it) => it.program_id === id)?.name ?? "";
  const picking = !!plan && chosen === null;
  const metres = (q?.program?.viewing_distance_mm ?? 1000) / 1000;
  const distanceText = metres === 1 ? "1 metre" : `${metres.toFixed(2)} metres`;

  return (
    <div className="home-test">
      <h1>Your vision test</h1>
      <ol className="steps">
        <li>Use the monitor you test on for the study, plugged in, in a normally lit room. If you have more than one
          screen, move this window onto that monitor.</li>
        <li>Sit <strong>{distanceText}</strong> from the screen (use the measuring cord you were given).</li>
        <li>Wear your usual glasses for distance.</li>
      </ol>

      {plan && <TodayCard plan={plan} chosen={chosen} nameOf={nameOf} onPick={(id) => { setChosen(id); setQ(null); }} />}

      {!q && !picking && (
        <button className="big" disabled={busy} onClick={() => void check()}>
          {busy ? "Checking…" : "Check my screen"}
        </button>
      )}
      {asking ? (
        <div className="card ask-card" role="status">
          <h2 style={{ marginTop: 0 }}>Chrome is asking you a question</h2>
          <ol className="steps fix-steps">
            <li>Look at the top-left of this Chrome window, just under the address bar.</li>
            <li>A small box asks to “Manage windows on all your displays”.</li>
            <li>Click <strong>Allow</strong>.</li>
          </ol>
        </div>
      ) : (
        <p className="small muted">If Chrome asks to “manage windows on all your displays”, choose <strong>Allow</strong>.</p>
      )}

      {q && !q.qualified && shown.length > 0 && (
        <div className="card bad-card">
          <h2 style={{ marginTop: 0 }}>{shown.length === 1 ? "One thing to fix first" : `${shown.length} things to fix`}</h2>
          {shown.map((c, i) => <FixCheck key={c.key} check={c} open={i === 0} />)}
          {root && <p className="small muted">The other screen checks run once this page is open in Google Chrome on a computer.</p>}
          <button className="big" disabled={busy} onClick={() => void check()}>Check again</button>
        </div>
      )}

      {q && needMonitor && (
        <MonitorHelper required={helperFailed.some((c) => c.critical)} report={q.monitor_id?.report ?? null}
          steps={q.monitor_id?.steps ?? {}} onDone={() => void check()} busy={busy} />
      )}

      {q && q.qualified && !q.demo && (
        <div className="card ok-card">
          <h2 style={{ marginTop: 0 }}>Your screen is ready</h2>
          <p>{q.model_name ?? (q.monitor_id?.report ? "Your monitor" : "")}</p>
          {q.monitor_class === "unapproved" && (
            <p className="small">Your monitor is not one the study has checked in the lab. You can use it; your results
              are kept with a note saying which monitor was used.</p>
          )}
          {q.instructions && <p>Display setting to use: <strong>{q.instructions}</strong></p>}
        </div>
      )}

      {q && q.qualified && advised.length > 0 && (
        <div className="card warn-card">
          <h2 style={{ marginTop: 0 }}>{advised.length === 1 ? "One thing worth fixing" : `${advised.length} things worth fixing`}</h2>
          <p className="small">You can still do the test. Fixing {advised.length === 1 ? "it" : "these"} makes the results more reliable.</p>
          {advised.map((c, i) => <FixCheck key={c.key} check={c} open={i === 0} />)}
          <button className="secondary" disabled={busy} onClick={() => void check()}>Check again</button>
        </div>
      )}

      {q && q.qualified && q.demo && (
        <div className="card demo-card">
          <h2 style={{ marginTop: 0 }}>Debug mode</h2>
          <p>An administrator turned the screen checks off for this account. Any screen can be used.</p>
          <p>Results are approximate: the screen size is {q.demo.size_source === "model"
            ? "taken from the approved monitor with this name" : "estimated"} and the screen is not calibrated.
            They are not part of your record.</p>
          {skipped.length > 0 && (
            <details className="small">
              <summary>Checks skipped ({skipped.length})</summary>
              <ul>{skipped.map((c) => <li key={c.key}><strong>{c.label}:</strong> {c.detail}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {q && q.qualified && q.brightness?.phase === "visit" && (
        <div className="card ok-card">
          <h2 style={{ marginTop: 0 }}>Brightness checked</h2>
          <p>Measured a few minutes ago, on this monitor: no need to measure again.</p>
        </div>
      )}
      {q && q.qualified && q.brightness && q.brightness.phase !== "visit" && lux === null && (
        <LuxGuide key={`${q.brightness.phase}-${q.brightness.edid_key}-${q.brightness.screen_key}-${fullGuide}`}
          variant={q.brightness.phase === "baseline" || fullGuide ? "full" : "short"}
          instructions={q.brightness.instructions}
          title={q.brightness.phase === "baseline"
            ? "First time on this monitor: your reading is saved as its normal brightness."
            : "Check that your screen is as bright as when it was set up."}
          judge={(r) => api.brightnessCheck(r, { edid_key: q.brightness!.edid_key, screen_key: q.brightness!.screen_key })}
          onDone={(r) => setLux(r)} onWantFull={() => setFullGuide(true)} />
      )}
      {q && q.qualified && q.brightness && q.brightness.phase !== "visit" && lux === null && !q.brightness.required && (
        luxSkipped ? (
          <p className="small muted">Brightness not measured today. <button className="link" onClick={() => setLuxSkipped(false)}>Measure it</button></p>
        ) : <p><button className="secondary" onClick={() => setLuxSkipped(true)}>Skip measuring today</button></p>
      )}
      {q && q.qualified && q.brightness && q.brightness.phase !== "visit" && lux !== null && (
        <div className="card ok-card">
          <h2 style={{ marginTop: 0 }}>Brightness checked</h2>
          <p>Your reading: {lux.join(", ")} lux. <button className="link" onClick={() => setLux(null)}>Measure again</button></p>
        </div>
      )}

      {q && q.qualified && brightnessOk && q.card?.phase === "visit" && (
        <div className="card ok-card"><p style={{ margin: 0 }}>Size checked with the card a few minutes ago: no need to again.</p></div>
      )}
      {q && q.qualified && brightnessOk && cardNeeded && card === null && q.screen_size_mm && q.screen_px && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Check the size with a bank card</h2>
          <CardCheck screenSizeMm={q.screen_size_mm} screenPx={q.screen_px} cardMm={q.card!.card_mm}
            tolerance={q.card!.tolerance} onDone={(scale) => setCard(scale)}
            onSkip={q.card!.level === "mandatory" ? undefined : () => setCard("skipped")} />
        </div>
      )}
      {q && q.qualified && brightnessOk && cardNeeded && card !== null && !cardOk && (
        <div className="card bad-card">
          <p style={{ marginTop: 0 }}>The outline you matched was {typeof card === "number" ? `${Math.round(Math.abs(card - 1) * 100)}%` : ""} off.
            Check that this page is not zoomed (Ctrl or ⌘ and 0 resets it) and that no screen magnifier is on.</p>
          <button className="big" onClick={() => setCard(null)}>Try the card again</button>
        </div>
      )}

      {q && q.qualified && brightnessOk && cardOk && (card !== null || !cardNeeded) && (
        <div className="card">
          <p>The test fills the screen. Answer with the arrow keys: the direction the E's bars point.
            If you cannot see it, press the space bar. Each eye takes a few minutes.</p>
          <p>Leaving the test, switching windows or moving it to another screen stops it, and that part will need repeating.</p>
          {distanceLevel !== "off" && (
            <label className="confirm">
              <input type="checkbox" checked={distanceOk} onChange={(e) => setDistanceOk(e.target.checked)} />
              I am sitting {distanceText} from the screen: the cord reaches from the screen to my eyes.
            </label>
          )}
          <button className="big" disabled={busy || (distanceLevel === "mandatory" && !distanceOk)} onClick={() => void start()}>Start the test</button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Today's programs: what runs now (in order, with what is done), or a choice. */
function TodayCard({ plan, chosen, nameOf, onPick }: {
  plan: Plan; chosen: number | null; nameOf: (id: number) => string; onPick: (id: number) => void;
}) {
  const done = new Set(plan.done_this_visit);
  const order = plan.mode === "all" || plan.mode === "schedule"
    ? [...plan.done_this_visit.filter((id) => !plan.run.includes(id)), ...plan.run] : [];
  if (plan.mode === "patient_picks" || (plan.mode === "schedule" && plan.run.length === 0)) {
    const offer = plan.mode === "patient_picks" ? plan.choose : plan.optional;
    return (
      <div className="card">
        {plan.mode === "schedule" && (
          <p style={{ marginTop: 0 }}><strong>Nothing is due today.</strong>
            {plan.next_due_at ? ` Your next test is due on ${new Date(plan.next_due_at).toLocaleDateString()}.` : ""}</p>
        )}
        {offer.length > 0 && (
          <>
            <h2 style={{ marginTop: 0 }}>{plan.mode === "patient_picks" ? "Which test would you like to do?" : "You can also do:"}</h2>
            <div className="program-choices">
              {offer.map((id) => (
                <button key={id} className={`big ${chosen === id ? "" : "secondary"}`} aria-pressed={chosen === id}
                  onClick={() => onPick(id)}>{nameOf(id)}</button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
  if (order.length <= 1) return null;
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Today: {order.length} tests, one after another</h2>
      <ol className="steps">
        {order.map((id) => (
          <li key={id} className={id === chosen ? "now" : ""}>{nameOf(id)}{done.has(id) ? " ✓" : id === chosen ? " — now" : ""}</li>
        ))}
      </ol>
    </div>
  );
}
